import { useState, useEffect } from "react";
import { History, Plus, Trash2, MessageSquare, Loader2 } from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from "@/components/ui/sheet";
import api from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

/**
 * Reusable chat-history drawer.
 * Props:
 *  - user            current user (drawer hidden when not signed in)
 *  - listUrl         GET endpoint returning the sessions array
 *  - normalize(data) -> [{ id, title, created_at }]
 *  - onSelect(id)    load a past conversation
 *  - onNew()         start a fresh conversation
 *  - deleteUrl(id)   -> string DELETE endpoint
 *  - activeId        currently open session
 *  - reloadKey       bump to force a reload (e.g. after a new message)
 *  - label           drawer title
 */
export const ChatHistory = ({
  user, listUrl, normalize, onSelect, onNew, deleteUrl, deleteAllUrl,
  activeId, reloadKey, label = "Chat History",
}) => {
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (!open || !user) return undefined;
    let active = true;
    queueMicrotask(() => { if (active) setLoading(true); });
    api.get(listUrl)
      .then(({ data }) => { if (active) setSessions(normalize(data)); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, reloadKey, user, listUrl, normalize]);

  const del = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(deleteUrl(id));
      setSessions((s) => s.filter((x) => x.id !== id));
      toast.success("Conversation deleted");
      if (id === activeId) onNew?.();
    } catch {
      toast.error("Could not delete conversation");
    }
  };

  const delAll = async () => {
    setClearing(true);
    try {
      await api.delete(deleteAllUrl);
      setSessions([]);
      setConfirmClear(false);
      toast.success("All history cleared");
      onNew?.();
    } catch {
      toast.error("Could not clear history");
    } finally {
      setClearing(false);
    }
  };

  if (!user) return null;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="chat-history-btn"
          className="flex items-center gap-2 glass-card rounded-full px-4 py-2 text-xs font-mono uppercase tracking-[0.14em] text-inkmuted hover:text-ink transition-all shrink-0"
        >
          <History className="w-4 h-4 text-gold" /> History
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="bg-canvas border-hairline text-ink w-[88vw] sm:max-w-sm overflow-y-auto"
        data-testid="chat-history-drawer"
      >
        <SheetHeader>
          <SheetTitle className="text-ink font-serif text-2xl">{label}</SheetTitle>
          <SheetDescription className="text-inkfaint text-xs">
            Your past conversations. Select one to reopen, or delete it.
          </SheetDescription>
        </SheetHeader>

        <button
          onClick={() => { onNew?.(); setOpen(false); }}
          data-testid="history-new-chat-btn"
          className="mt-6 w-full flex items-center justify-center gap-2 rounded-full bg-btn text-btnink hover:bg-btnhover px-5 py-3 text-sm font-semibold uppercase tracking-wider transition-all"
        >
          <Plus className="w-4 h-4" /> New Chat
        </button>

        <div className="mt-6 space-y-2" data-testid="history-list">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-inkfaint">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : sessions.length === 0 ? (
            <p className="text-center text-sm text-inkfaint py-12" data-testid="history-empty">
              No conversations yet.
            </p>
          ) : (
            sessions.map((s) => (
              <div
                key={s.id}
                onClick={() => { onSelect(s.id); setOpen(false); }}
                data-testid="history-item"
                className={`group flex items-center gap-3 rounded-xl px-3 py-3 cursor-pointer border transition-all ${
                  s.id === activeId
                    ? "border-gold/40 bg-surface"
                    : "border-hairline hover:border-hairline hover:bg-surface"
                }`}
              >
                <MessageSquare className="w-4 h-4 text-gold shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-ink truncate">{s.title || "Conversation"}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-inkfaint mt-0.5">
                    {fmtDate(s.created_at)}
                  </p>
                </div>
                <button
                  onClick={(e) => del(e, s.id)}
                  data-testid="history-delete-btn"
                  title="Delete conversation"
                  className="text-inkfaint hover:text-red-400 transition-colors shrink-0 p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>

        {deleteAllUrl && sessions.length > 0 && (
          <div className="mt-6 pt-4 border-t border-hairline" data-testid="history-clear-all-block">
            {!confirmClear ? (
              <button
                onClick={() => setConfirmClear(true)}
                data-testid="history-clear-all-btn"
                className="w-full flex items-center justify-center gap-2 rounded-full border border-red-400/40 text-red-500 hover:bg-red-500/10 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider transition-all"
              >
                <Trash2 className="w-4 h-4" /> Delete all history
              </button>
            ) : (
              <div className="space-y-2.5" data-testid="history-clear-confirm">
                <p className="text-xs text-inkmuted text-center">Delete all conversations? This can&apos;t be undone.</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setConfirmClear(false)}
                    data-testid="history-clear-cancel-btn"
                    className="flex-1 rounded-full border border-hairline text-inkmuted hover:text-ink px-4 py-2.5 text-xs font-semibold uppercase tracking-wider transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={delAll}
                    disabled={clearing}
                    data-testid="history-clear-all-confirm-btn"
                    className="flex-1 flex items-center justify-center gap-2 rounded-full bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider transition-all"
                  >
                    {clearing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete all
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default ChatHistory;
